import ISO6391 from 'iso-639-1';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    availableLanguages,
    changeDateLocale,
    changeI18nLanguage,
    changeNumberLocale,
    DateLocale,
    Datepicker,
    DatepickerV2,
    getCurrentI18nLanguage,
    getDateLocale,
    getNumberLocale,
    i18n,
    Input,
    LanguageCode,
    toCurrencyStringOrUndefined,
    toZonedDateStringOrUndefined
} from '@client/toolkit';

import { DebugButton } from './debug-button.component';
import { DebugComment } from './debug-comment.component';
import { DebugInput } from './debug-input.component';
import { DebugPre } from './debug-pre.component';
import { DebugSelect } from './debug-select.component';
import dayjs from 'dayjs';

const ALLOWED_LANGUAGE_CODES: LanguageCode[] = availableLanguages.map((x) => x.code);

// sort by name
const LANGUAGES = ISO6391.getAllCodes()
  .map((x) => ({ code: x, name: ISO6391.getName(x) }))
  .filter((x) => ALLOWED_LANGUAGE_CODES.includes(x.code))
  .sort((a, b) => (a.name > b.name ? 0 : -1));

const DATE_LOCALES: DateLocale[] = availableLanguages.map((x) => x.dateLocale);

export function DebugI18n() {

  const { t } = useTranslation('app');

  const [inputNumberValue, setInputNumberValue] = useState('20.22');
  const [datepickerValue, setDatepickerValue] = useState<Date | null>(new Date());

  const [i18nLanguageState, setI18nLanguageState] = useState(getCurrentI18nLanguage());
  const [i18nLanguageCodeValue, setI18nLanguageCodeValue] = useState(i18nLanguageState.code);

  const [i18nCodeState, setI18nCodeState] = useState(i18n.resolvedLanguage);
  const [i18nCodeValue, setI18nCodeValue] = useState(i18nCodeState);

  const [dateLocaleState, setDateLocaleState] = useState(getDateLocale());
  const [dateLocaleValue, setDateLocaleValue] = useState(dateLocaleState);

  const [numberLocaleState, setNumberLocaleState] = useState(getNumberLocale());
  const [numberLocaleValue, setNumberLocaleValue] = useState(numberLocaleState);

  const [inputLanguageState, setInputLanguageState] = useState(getCurrentI18nLanguage().inputLang);

  const handleChangeI18nLanguage = async () => {
    await changeI18nLanguage(i18nLanguageCodeValue)
    setI18nLanguageState(getCurrentI18nLanguage());
    setInputLanguageState(getCurrentI18nLanguage().inputLang);

    // also change all other fields
    setI18nCodeValue(i18n.resolvedLanguage);
    setI18nCodeState(i18n.resolvedLanguage);
    setDateLocaleValue(getCurrentI18nLanguage().dateLocale);
    setDateLocaleState(getCurrentI18nLanguage().dateLocale);
    setNumberLocaleValue(getCurrentI18nLanguage().numberLocale);
    setNumberLocaleState(getCurrentI18nLanguage().numberLocale);
  }

  const handleChangeI18nCode = async () => {
    await i18n.changeLanguage(i18nCodeValue);
    setI18nCodeState(i18n.resolvedLanguage);
  }

  const handleChangeDateLocale = () => {
    if (!dateLocaleValue) return;
    changeDateLocale(dateLocaleValue);
    setDateLocaleState(getDateLocale());
  }

  const handleChangeNumberLocale = () => {
    if (!numberLocaleValue) return;
    changeNumberLocale(numberLocaleValue);
    setNumberLocaleState(getNumberLocale());
  }

  return (
    <article className="space-y-12">
      <div className="space-y-1">
        <DebugComment>
          i18n includes date & number locale information but those two utils store the value for themselfs as well. Components will mostly use the attributes from i18n language object.
        </DebugComment>

      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <div>Example locize text:</div>
          <DebugPre>
            {t('debugText', { defaultValue: 'Translation not found!' })}
          </DebugPre>
        </div>
        <div className="space-y-2">
          <div>Example date string:</div>
          <DebugPre onClick={() => console.log({ dateString: toZonedDateStringOrUndefined(new Date()) })}>
            {JSON.stringify(toZonedDateStringOrUndefined(new Date()))}
          </DebugPre>
        </div>
        <div className="space-y-2">
          <div>Example currency string:</div>
          <DebugPre onClick={() => console.log({ currencyString: toCurrencyStringOrUndefined(212345.93) })}>
            {JSON.stringify(toCurrencyStringOrUndefined(212345.93))}
          </DebugPre>
        </div>
      </div>

      <div className="space-y-4">
        <div>Affected components</div>

        <div className="space-y-2">
          <div className="flex">
            <div className="font-medium">
              {'<Input />'}
            </div>
            <DebugComment className="ml-1">Only for number inputs | Uses inputLang prop from i18n</DebugComment>
          </div>
          <div className="flex text-black">
            <Input type="number" value={inputNumberValue} onChange={(e) => setInputNumberValue(e.target.value as any)} />
          </div>
          <DebugPre onClick={() => console.log({ inputNumberValue: inputNumberValue })}>
            {inputNumberValue || '\xa0'}
          </DebugPre>
        </div>

        <div className="space-y-2">
          <div className="flex">
            <div className="font-medium">
              {'<Datepicker />'}
            </div>
            <DebugComment className="ml-1">Uses dateLocale prop from i18n</DebugComment>
          </div>
          <div className="flex text-black">
            <Datepicker selected={datepickerValue} onChange={(value) => setDatepickerValue(value)} />
          </div>
          <DebugPre onClick={() => console.log({ datepickerValue: datepickerValue })}>
            {datepickerValue ? JSON.stringify(datepickerValue) : '\xa0'}
          </DebugPre>
        </div>


        <div className="space-y-2">
          <div className="flex">
            <div className="font-medium">
              {'<DatepickerV2 />'}
            </div>
            <DebugComment className="ml-1">Uses dateLocale prop from i18n</DebugComment>
          </div>
          <div className="flex text-black">
            <DatepickerV2 value={dayjs(datepickerValue)} onChange={(value) => setDatepickerValue(value?.toDate() ?? null)} />
          </div>
          <DebugPre onClick={() => console.log({ datepickerValue: datepickerValue })}>
            {datepickerValue ? JSON.stringify(datepickerValue) : '\xa0'}
          </DebugPre>
        </div>

      </div>

      <div className="space-y-2">
        <div>
          Current i18nLanguage:
        </div>
        <DebugPre onClick={() => console.log({ i18nLanguage: i18nLanguageState })}>
          {JSON.stringify(i18nLanguageState)}
        </DebugPre>

        <div className="flex space-x-2">
          <DebugSelect value={i18nLanguageCodeValue} onChange={(e) => setI18nLanguageCodeValue(e.target.value as any)}>
            <option value="" />
            {LANGUAGES.map((language, idx) => (
              <option key={idx} value={language.code}>
                {language.name}
              </option>
            ))}
          </DebugSelect>
          <DebugButton onClick={handleChangeI18nLanguage} disabled={i18nLanguageState.code === i18nLanguageCodeValue}>
            Apply
          </DebugButton>
          <DebugButton onClick={() => { setI18nLanguageCodeValue(i18nLanguageState.code) }} disabled={i18nLanguageState.code === i18nLanguageCodeValue}>
            Reset
          </DebugButton>
        </div>
        <DebugComment>
          Will affect all i18n & locale related values
        </DebugComment>
      </div>

      <div className="space-y-2">
        <div>
          Current i18nLanguageCode:
        </div>
        <DebugPre onClick={() => console.log({ i18nLanguageCode: i18nCodeState })}>
          {i18nCodeState}
        </DebugPre>

        <div className="flex space-x-2">
          <DebugInput value={i18nCodeValue} onChange={(e) => setI18nCodeValue(e.target.value)} />
          <DebugButton onClick={handleChangeI18nCode} disabled={i18nCodeState === i18nCodeValue}>
            Apply
          </DebugButton>
          <DebugButton onClick={() => { setI18nCodeValue(i18nCodeState) }} disabled={i18nCodeState === i18nCodeValue}>
            Reset
          </DebugButton>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          Current date locale:
        </div>
        <DebugPre onClick={() => console.log({ dateLocale: dateLocaleState })}>
          {JSON.stringify(dateLocaleState)}
        </DebugPre>

        <div className="flex space-x-2">
          <DebugSelect value={dateLocaleValue?.code} onChange={(e) => setDateLocaleValue(e.target.value as any)}>
            <option value="" />
            {DATE_LOCALES.map((locale, idx) => (
              <option key={idx} value={locale.code}>
                {locale.code}
              </option>
            ))}
          </DebugSelect>
          <DebugButton onClick={handleChangeDateLocale} disabled={dateLocaleState === dateLocaleValue || !dateLocaleValue}>
            Apply
          </DebugButton>
          <DebugButton onClick={() => { setDateLocaleValue(dateLocaleState) }} disabled={dateLocaleState === dateLocaleValue}>
            Reset
          </DebugButton>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          Current number locale:
        </div>
        <DebugPre onClick={() => console.log({ numberLocale: numberLocaleState })}>
          {numberLocaleState}
        </DebugPre>

        <div className="flex space-x-2">
          <DebugInput value={numberLocaleValue as any} onChange={(e) => setNumberLocaleValue(e.target.value as any)} />
          <DebugButton onClick={handleChangeNumberLocale} disabled={numberLocaleState === numberLocaleValue || !numberLocaleValue}>
            Apply
          </DebugButton>
          <DebugButton onClick={() => { setNumberLocaleValue(numberLocaleState) }} disabled={numberLocaleState === numberLocaleValue}>
            Reset
          </DebugButton>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          Current input lang:
        </div>
        <DebugPre onClick={() => console.log({ inputLang: inputLanguageState })}>
          {inputLanguageState}
        </DebugPre>

        <DebugComment>
          Part of i18n object and can only be changed as part of it
        </DebugComment>
      </div>
    </article>
  )

}
